import React, { useState, useEffect, memo, useMemo } from 'react';
import { logger } from '../utils/logger';

const ChildProgressView = memo(({ child, onClose }) => {
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [dateRange, setDateRange] = useState('all'); // 'week', 'month', 'all'

  // Extract subjects from progress
  const subjects = useMemo(() => {
    if (!child?.progress?.completedSubjects) return [];
    return Object.keys(child.progress.completedSubjects);
  }, [child]);

  // Calculate subject statistics
  const subjectStats = useMemo(() => {
    if (!child?.progress?.completedSubjects) return {};
    
    const stats = {};
    subjects.forEach(subject => {
      const subjectData = child.progress.completedSubjects[subject];
      const totalQuestions = subjectData?.questionsAnswered || 0;
      const correctAnswers = subjectData?.correctAnswers || 0;
      const accuracy = totalQuestions > 0 ? (correctAnswers / totalQuestions) * 100 : 0;
      
      stats[subject] = {
        name: subject,
        displayName: getSubjectDisplayName(subject),
        score: subjectData?.score || 0,
        totalQuestions,
        correctAnswers,
        accuracy: Math.round(accuracy),
        timeSpent: subjectData?.timeSpent || 0,
        lastPlayed: subjectData?.lastPlayed,
        completed: subjectData?.completed || false
      };
    });
    
    return stats;
  }, [child, subjects]);

  // Identify weak and strong areas
  const { weakAreas, strongAreas } = useMemo(() => {
    const weak = [];
    const strong = [];
    
    Object.values(subjectStats).forEach(stat => {
      if (stat.totalQuestions > 0) {
        if (stat.accuracy < 70) {
          weak.push(stat);
        } else if (stat.accuracy >= 85) {
          strong.push(stat);
        }
      }
    });
    
    return {
      weakAreas: weak.sort((a, b) => a.accuracy - b.accuracy),
      strongAreas: strong.sort((a, b) => b.accuracy - a.accuracy)
    };
  }, [subjectStats]);

  // Format time spent
  const formatTime = (minutes) => {
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  // Get subject display name
  function getSubjectDisplayName(subject) {
    const names = {
      phonics: 'Phonics',
      math: 'Math',
      reading: 'Reading',
      spelling: 'Spelling',
      science: 'Science',
      social: 'Social Studies',
      art: 'Art',
      geography: 'Geography',
      history: 'History',
      skipcounting: 'Skip Counting'
    };
    return names[subject] || subject.charAt(0).toUpperCase() + subject.slice(1);
  }

  if (!child) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Select a child to view their progress</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {child.display_name}'s Detailed Progress
          </h2>
          <p className="text-gray-600 mt-1">
            Last active: {child.progress?.last_active ? 
              new Date(child.progress.last_active).toLocaleDateString() : 
              'Never'}
          </p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800"
          >
            ✕ Close
          </button>
        )}
      </div>

      {/* Overall Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-purple-600">
            {child.progress?.score || 0}
          </div>
          <div className="text-sm text-gray-600">Total Points</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-green-600">
            {child.progress?.learning_streak || 0}
          </div>
          <div className="text-sm text-gray-600">Learning Streak</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-blue-600">
            {subjects.length}
          </div>
          <div className="text-sm text-gray-600">Subjects Studied</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-orange-600">
            {child.progress?.completed_lessons?.length || 0}
          </div>
          <div className="text-sm text-gray-600">Lessons Completed</div>
        </div>
      </div>

      {/* Subject Breakdown */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Subject Performance</h3>
        <div className="space-y-4">
          {subjects.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No subjects studied yet</p>
          ) : (
            subjects.map(subject => {
              const stat = subjectStats[subject];
              return (
                <div
                  key={subject}
                  className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                    selectedSubject === subject
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                  onClick={() => setSelectedSubject(selectedSubject === subject ? null : subject)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-900">{stat.displayName}</h4>
                    <div className="text-sm text-gray-600">
                      {stat.score} points
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 text-sm mb-2">
                    <div>
                      <span className="text-gray-600">Accuracy:</span>
                      <span className={`ml-2 font-semibold ${
                        stat.accuracy >= 85 ? 'text-green-600' :
                        stat.accuracy >= 70 ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {stat.accuracy}%
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Questions:</span>
                      <span className="ml-2 font-semibold">{stat.totalQuestions}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Time:</span>
                      <span className="ml-2 font-semibold">{formatTime(stat.timeSpent)}</span>
                    </div>
                  </div>
                  
                  {/* Accuracy Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        stat.accuracy >= 85 ? 'bg-green-500' :
                        stat.accuracy >= 70 ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}
                      style={{ width: `${Math.min(stat.accuracy, 100)}%` }}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Weak Areas */}
      {weakAreas.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-red-900 mb-4 flex items-center gap-2">
            <span>⚠️</span>
            <span>Areas Needing Attention</span>
          </h3>
          <div className="space-y-3">
            {weakAreas.map(area => (
              <div key={area.name} className="bg-white rounded p-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900">{area.displayName}</span>
                  <span className="text-red-600 font-semibold">{area.accuracy}% accuracy</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {area.totalQuestions} questions answered
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Strong Areas */}
      {strongAreas.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-green-900 mb-4 flex items-center gap-2">
            <span>🌟</span>
            <span>Strong Areas</span>
          </h3>
          <div className="space-y-3">
            {strongAreas.map(area => (
              <div key={area.name} className="bg-white rounded p-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900">{area.displayName}</span>
                  <span className="text-green-600 font-semibold">{area.accuracy}% accuracy</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {area.totalQuestions} questions answered
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Selected Subject Details */}
      {selectedSubject && subjectStats[selectedSubject] && (
        <div className="bg-white rounded-lg shadow p-6 border-2 border-purple-500">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-900">
              {subjectStats[selectedSubject].displayName} Details
            </h3>
            <button
              onClick={() => setSelectedSubject(null)}
              className="text-gray-600 hover:text-gray-800"
            >
              ✕
            </button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-sm text-gray-600">Score</div>
              <div className="text-2xl font-bold text-purple-600">
                {subjectStats[selectedSubject].score}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Accuracy</div>
              <div className="text-2xl font-bold text-green-600">
                {subjectStats[selectedSubject].accuracy}%
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Questions</div>
              <div className="text-2xl font-bold text-blue-600">
                {subjectStats[selectedSubject].totalQuestions}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Time Spent</div>
              <div className="text-2xl font-bold text-orange-600">
                {formatTime(subjectStats[selectedSubject].timeSpent)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

ChildProgressView.displayName = 'ChildProgressView';

export default ChildProgressView;

